import { isEmpty } from 'lodash';
import * as Sentry from '@sentry/browser';
import { IApiError, IApiResponse } from '../types';

const BUILD_ENVIRONMENT = process.env.BUILD_ENVIRONMENT;

interface ISentryScopeConfig {
	tags: Record<string, string>;
	user?: Record<string, unknown>;
	level?: Sentry.SeverityLevel;
	extras?: Record<string, unknown>;
}

interface IGetSentryErrorParams {
	error: IApiError;
	errorType?: string;
}

// This function is to rename the error name for sentry
function getSentryError({
	error,
	errorType,
}: IGetSentryErrorParams): IApiError {
	const sentryErrorObj = new Error(error.message) as IApiError;
	sentryErrorObj.stack = error.stack;
	sentryErrorObj.name = errorType ? `${errorType} Error` : error.name;

	if (error.isAxiosError) {
		sentryErrorObj.name = `${sentryErrorObj.name}: ${error.response?.status}`;
		sentryErrorObj.toJSON = error.toJSON;
		sentryErrorObj.config = error.config;
		sentryErrorObj.isAxiosError = error.isAxiosError;
		sentryErrorObj.request = error.request;
		sentryErrorObj.response = error.response;
	}
	return sentryErrorObj;
}

export function captureException(error: IApiError, config: ISentryScopeConfig) {
	if (BUILD_ENVIRONMENT === 'development') return;

	// These tags, levels, extras are set locally, will send only with current exception.
	const { tags = {}, level = 'error', extras = {} } = config;
	if (!isEmpty(tags) || !isEmpty(level) || !isEmpty(extras)) {
		const errorObj = getSentryError({ error, errorType: tags.errorType });

		Sentry.withScope((scope) => {
			Object.keys(tags).forEach((key) => {
				scope.setTag(key, tags[key]);
			});
			Object.keys(extras).forEach((key) => {
				scope.setExtra(key, extras[key]);
			});
			level && scope.setLevel(level);
			Sentry.captureException(errorObj);
		});
	} else {
		Sentry.captureException(error, {
			level: 'error',
		});
	}
}

// This function is to set the tags on sentry GLOBALLY, please keep this in mind
export function configureSentryScope(config: ISentryScopeConfig) {
	if (BUILD_ENVIRONMENT === 'development') return;

	const { tags = {}, user = {} } = config;
	Sentry.configureScope((scope) => {
		Object.keys(tags).forEach((key) => {
			scope.setTag(key, tags[key]);
		});
		!isEmpty(user) && scope.setUser(user);
	});
}

/**
 *
 * @param {Sentry.BrowserOptions} initConfig
 */
export function sentryInit(initConfig = {}) {
	if (BUILD_ENVIRONMENT === 'development') return;

	Sentry.init(initConfig);
}
